<template>
  <div class="network-status-container">
    <!-- 网络离线提示 -->
    <el-alert
      v-if="!isOnline && showAlerts"
      type="error"
      :closable="false"
      show-icon
      class="network-alert"
    >
      <template #title>
        <span class="alert-title">{{ t('report.networkDisconnected') }}</span>
      </template>
      <template #default>
        <div class="alert-content">
          {{ t('report.networkDisconnectedDesc') }}
          <el-button size="small" type="primary" @click="checkConnection">{{ t('report.retryConnection') }}</el-button>
        </div>
      </template>
    </el-alert>

    <!-- 后端服务不可用提示 -->
    <el-alert
      v-if="isOnline && !isBackendAvailable && showAlerts"
      type="warning"
      :closable="false"
      show-icon
      class="network-alert"
    >
      <template #title>
        <span class="alert-title">{{ t('report.backendUnavailable') }}</span>
      </template>
      <template #default>
        <div class="alert-content">
          {{ t('report.backendUnavailableDesc') }}
          <el-button size="small" type="primary" @click="checkBackendService">{{ t('report.retryConnection') }}</el-button>
        </div>
      </template>
    </el-alert>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
import { useNetworkStore } from '@/stores/network';
import { checkBackendHealth } from '@/utils/request';

// 网络状态管理
const networkStore = useNetworkStore();
const isOnline = computed(() => networkStore.isOnline);
const isBackendAvailable = computed(() => networkStore.isBackendAvailable);
const reconnectCount = computed(() => networkStore.reconnectCount);

// 是否显示警告提示，默认不显示
const showAlerts = ref(false);

// 用于存储检查定时器ID
const healthCheckIntervalId = ref(null);

// 记录上次健康检查时间
const lastHealthCheckTime = ref(Date.now());

// 检查网络连接
const checkConnection = async () => {
  try {
    // 更新在线状态
    networkStore.setOnline(navigator.onLine);
    
    // 如果在线，检查后端服务
    if (isOnline.value) {
      networkStore.incrementReconnectCount();
      await checkBackendService();
    }
  } catch (error) {
    console.error('检查网络连接失败:', error);
  }
};

// 检查后端服务健康状态
const checkBackendService = async () => {
  // 如果距离上次检查不到10秒，跳过本次检查
  const now = Date.now();
  if (now - lastHealthCheckTime.value < 10000) {
    console.log('跳过健康检查，距上次检查时间过短', 
      Math.floor((now - lastHealthCheckTime.value) / 1000), t('common.secondShort'));
    return;
  }
  
  // 更新上次检查时间
  lastHealthCheckTime.value = now;
  
  try {
    console.log('开始执行健康检查...');
    
    // 调用后端健康检查API
    const isHealthy = await checkBackendHealth();
    
    // 更新后端服务状态
    networkStore.setBackendAvailable(isHealthy);
    
    console.log('健康检查完成，服务状态:', isHealthy);
  } catch (error) {
    console.error('检查后端服务失败:', error);
    networkStore.setBackendAvailable(false);
  }
};

// 监听网络状态变化
const handleOnline = () => {
  networkStore.setOnline(true);
  checkBackendService();
};

const handleOffline = () => {
  networkStore.setOnline(false);
};

onMounted(() => {
  // 初始化网络状态
  networkStore.setOnline(navigator.onLine);
  
  // 添加网络状态事件监听器
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  // 设置定时检查后端服务
  healthCheckIntervalId.value = setInterval(async () => {
    // 只在在线状态下检查后端服务
    if (isOnline.value) {
      await checkBackendService();
    }
  }, 6*60*60*1000); // 从3小时增加到6小时，进一步减少检查频率
  
  // 首次检查 - 延迟30秒执行，避免应用启动时立即执行健康检查
  setTimeout(() => {
    checkBackendService();
  }, 30000);
});

onBeforeUnmount(() => {
  // 移除网络状态事件监听器
  window.removeEventListener('online', handleOnline);
  window.removeEventListener('offline', handleOffline);
  
  // 清除定时器
  if (healthCheckIntervalId.value) {
    clearInterval(healthCheckIntervalId.value);
  }
});
</script>

<style scoped>
.network-status-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
}

.network-alert {
  margin-bottom: 0;
}

.alert-title {
  font-weight: bold;
}

.alert-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style> 